import { CardModel } from "atoms/models";
import CardBenefits from "components/Browse/CardBenefits";
import CardBroadbandLabel from "./CardBroadbandLabel";
import { postBrowsePage } from "services/post-browsePage";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { toastState, overlayState, browseBannerState, broadbandLabelAccordionOpenState, cartState } from "atoms";
import { useNavigate } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { useState } from "react";
import { BrowseBannerModel } from "atoms/models";
import TVIcon from "images/StreamTV-Illustration.svg";

interface Item {
  serviceCoreTypeName: string;
}

interface FieldName {
  fieldName: string;
}

const Card: React.FC<{ card: CardModel }> = ({ card }) => {
  const [toast, setToast] = useRecoilState(toastState);
  const [showStreamTvModal, setShowStreamTvModal] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [broadbandLabelAccordionOpen, setBroadbandLabelAccordionOpen] = useRecoilState(broadbandLabelAccordionOpenState(card?.id));
  const { contentEcommerce } = card;
  const navigate = useNavigate();
  const setShowOverlay = useSetRecoilState(overlayState);
  const setBanner = useSetRecoilState<BrowseBannerModel>(browseBannerState);
  const [selectedOption, setSelectedOption] = useState<string | null>("");

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const header = contentEcommerce.find((obj: FieldName) => obj.fieldName === "heading");
  const subHeading = contentEcommerce.find((obj: FieldName) => obj.fieldName === "subHeading");
  const price = card?.displayPrice?.split(".");
  const dollars = price && price[0] ? price[0] : null;
  const cents = price && price[1] ? price[1] : null;
  const offerTerm = card?.offerTerm;
  const offerTermContentCatalog = contentEcommerce.find((obj: FieldName) => obj.fieldName === "offerTerm");
  const broadbandLabel = card?.label;
  const cart = useRecoilValue(cartState);

  const hasInternetFromCartProducts = cart?.list?.cartProducts?.some(
    (item: Item) => item.serviceCoreTypeName === "HSI" || item.serviceCoreTypeName === "INT"
  );
  const hasInternetFromUnchangedProducts = cart?.list?.unchangedProducts?.some(
    (item: Item) => item.serviceCoreTypeName === "HSI" || item.serviceCoreTypeName === "INT"
  );

  const handleAddToCart = async () => {
    setShowOverlay(true);
    const response: any = await postBrowsePage(card.cardId);

    if (response?.ecommerceAction === "error") {
      setShowStreamTvModal(false);
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: `${response?.message}`,
      });
      setShowOverlay(false);
      setSpinner(false);
      return;
    } else {
      if (response?.["_links"]?.["ec:windowLocation"]?.href) {
        setBanner({} as BrowseBannerModel);
        navigate(response?.["_links"]?.["ec:windowLocation"]?.href);
      }
      setSpinner(false);
    }
  };

  return (
    <div className="card-container">
      <div className="card">
        <div className="card-heading text-center">{header?.content}</div>
        <div className="card-body text-center">
          <h5 className="card-title-sub-heading">{subHeading?.content}</h5>
          <div className="card-display-price">
            <div className="dollars">
              <strong>{dollars}</strong>
            </div>
            <div className="cents-and-offer-term">
              <div className="cents">{cents}</div>
              {offerTermContentCatalog && offerTerm && offerTerm !== "0" && offerTerm !== "360" ? (
                <div className="offer-term">for {offerTerm} months</div>
              ) : null}
              {card?.label?.monthlyPrice && card.offerTerm && offerTerm !== "0" && offerTerm !== "360" ? (
                <div className="rack-rate">then ${card.label.monthlyPrice}</div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="cart-btn-container">
          <button
            className="btn-cart"
            onClick={() => {
              // Customer must have Internet in their cart to add Stream Tv service. If no internet in the cart then call the service
              // which will display an error. If has INT then fire the strem TV modal
              card.streamTv && (hasInternetFromCartProducts || hasInternetFromUnchangedProducts) ? setShowStreamTvModal(true) : handleAddToCart();
              setBroadbandLabelAccordionOpen(false);
              window.scroll(0, 0);
            }}
          >
            Add to Cart
          </button>
        </div>
        {broadbandLabel && Object.keys(broadbandLabel).length > 0 ? (
          <div className="mt-3">
            {!broadbandLabelAccordionOpen ? <CardBenefits card={card} showSpeedsOnly={true} /> : null}
            <CardBroadbandLabel broadbandLabel={broadbandLabel} card={card} />
          </div>
        ) : (
          <CardBenefits card={card} showSpeedsOnly={false} />
        )}
      </div>
      <Modal
        show={showStreamTvModal}
        onHide={() => {
          setShowStreamTvModal(false);
          setSelectedOption(null);
        }}
        size="lg"
        className="stream-tv-modal"
      >
        <Modal.Body>
          <div className="stream-tv-content-container">
            <div className="stream-tv-image">
              <img src={TVIcon} className="stream-tv-svg" alt="" />
            </div>
            <div className="stream-tv-content">
              <div className="text-end">
                <i
                  className="fa-solid fa-xmark"
                  onClick={() => {
                    setShowStreamTvModal(false);
                    setSelectedOption(null);
                  }}
                ></i>
              </div>
              <h2 className="stream-tv-tagline">A compatible streaming device or smart TV is required for Comporium TV.</h2>
              <div className="stream-tv-copy">
                <div className="mb-1">
                  <strong>Popular Devices:</strong>
                </div>
                <div className="mb-1">
                  Fire TV Stick (4K, Gen 3 &amp; Gen 2)
                  <br />
                  Apple devices
                </div>
                <div>Don't see your device?</div>
              </div>
              <div className="stream-tv-device-link">
                <span onClick={() => window.open("https://help.comporium.com/s/article/Where-can-I-watch-Stream-TV", "_blank")}>
                  Check for compatible device/smart TV
                </span>
              </div>
              <div className="stream-tv-radio-container">
                <form>
                  <div>
                    <div>
                      <label className="me-3 mb-2 stream-tv-options-radio">
                        <input type="radio" value="option1" checked={selectedOption === "option1"} onChange={handleOptionChange} /> I have a
                        compatible device.
                      </label>
                    </div>
                    <div>
                      <label className="stream-tv-options-radio">
                        <input type="radio" value="option2" checked={selectedOption === "option2"} onChange={handleOptionChange} /> I will purchase
                        later.
                      </label>
                    </div>
                  </div>
                </form>
              </div>
              <div className="stream-tv-button-spinner-container">
                {spinner ? <Spinner animation="border" className="me-3" /> : null}
                <button
                  className="btn btn-primary btn-lg stream-tv-continue-button"
                  disabled={!selectedOption}
                  onClick={() => {
                    setSpinner(true);
                    handleAddToCart();
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Card;
